/**
 * 资源库三语文案分片 · 素材上传（由 strings.ts 聚合）
 * 覆盖 /resource 素材上传弹窗、多文件上传列表与相关校验工具的用户可见文案。
 */
export const resPartUpload = {
  // ── 通用：标题 / 按钮 / 状态 ──
  /** 多文件上传弹窗与单文件上传弹窗标题 */
  resUploadTitle: { en: "Upload media", zh: "上传素材", id: "Unggah materi" },
  /** 移动端弹窗右上角「上传文件」按钮 */
  resUploadUploadFile: { en: "Upload files", zh: "上传文件", id: "Unggah file" },
  /** 拖放区主提示 */
  resUploadDropzoneMain: {
    en: "Click to choose files, or drag and drop them here to upload",
    zh: "点击选择文件，或将文件拖拽到此处上传",
    id: "Klik untuk memilih file, atau seret dan lepas file ke sini untuk mengunggah",
  },
  /** 全部文件被排除时主按钮文案 */
  resUploadReselectFiles: { en: "Choose files again", zh: "重新选择文件", id: "Pilih file lagi" },
  /** 确认前名称预检进行中 */
  resUploadCheckingNames: { en: "Checking names...", zh: "校验名称中", id: "Memeriksa nama..." },
  /** 确认按钮 */
  resUploadDoneUpload: { en: "Finish upload", zh: "完成上传", id: "Selesaikan unggahan" },
  /** 桌面端底部左侧继续追加文件 */
  resUploadKeepSelecting: { en: "Add more files", zh: "继续选择文件", id: "Tambah file lagi" },
  /** 名称预检请求失败提示 */
  resUploadNameCheckFailed: {
    en: "Name check failed. Please try again later.",
    zh: "名称校验失败，请稍后重试",
    id: "Gagal memeriksa nama, coba lagi nanti.",
  },
  /** 上传任务卡片进度标签 */
  resUploadUploading: { en: "Uploading…", zh: "上传中…", id: "Mengunggah…" },
  /** 视频转码进度标签 */
  resUploadTranscoding: { en: "Transcoding…", zh: "转码中…", id: "Mengonversi…" },
  /** 上传完成后写入素材记录阶段 */
  resUploadPersisting: { en: "Saving to library…", zh: "入库中…", id: "Menyimpan ke pustaka…" },
  /** 多文件批量失败聚合提示 */
  resUploadBatchFailCount: {
    en: "{n} files failed to upload. Please try again.",
    zh: "{n} 个文件上传失败，请重试",
    id: "{n} file gagal diunggah, silakan coba lagi.",
  },
  /** 批量创建待入库记录失败 */
  resUploadCreateTasksFailed: {
    en: "Failed to create upload tasks. Please try again.",
    zh: "创建上传任务失败，请重试",
    id: "Gagal membuat tugas unggahan, silakan coba lagi.",
  },
  /** 无名称文件默认名 */
  resUploadUnnamed: { en: "Untitled", zh: "未命名", id: "Tanpa nama" },

  // ── 多文件列表 ──
  /** 横幅：整批全部被排除 */
  resUploadAllRejected: {
    en: "All {n} selected files failed the format requirements and were excluded",
    zh: "所选 {n} 个文件均不符合格式要求，已排除",
    id: "Semua {n} file yang dipilih tidak memenuhi syarat format dan telah dikecualikan",
  },
  /** 横幅：部分文件被排除 */
  resUploadSomeRejected: {
    en: "{n} files failed the format requirements and were excluded!",
    zh: "{n} 个文件不符合格式要求，已排除！",
    id: "{n} file tidak memenuhi syarat format dan telah dikecualikan!",
  },
  /** 横幅右侧展开详情 */
  resUploadViewDetail: { en: "View details", zh: "查看详情", id: "Lihat detail" },
  /** 行内名称输入占位 */
  resUploadNamePlaceholder: { en: "File name", zh: "文件名称", id: "Nama file" },
  /** 行内描述输入占位 */
  resUploadDescPlaceholder: { en: "Add a description (optional)", zh: "添加描述（选填）", id: "Tambahkan deskripsi (opsional)" },
  /** 视频行口播开关：关闭态 */
  resUploadNarrationNo: { en: "No narration", zh: "无口播", id: "Tanpa sulih suara" },
  /** 视频行口播开关：开启态 */
  resUploadNarrationYes: { en: "With narration", zh: "有口播", id: "Dengan sulih suara" },
  /** 行右侧校验进行中状态 */
  resUploadValidating: { en: "Validating", zh: "校验中", id: "Memvalidasi" },
  /** 空态提示三行之一：批量说明 */
  resUploadHintBatch: {
    en: "Select multiple files of different types at once, up to 50 files per batch",
    zh: "支持一次选择多个不同类型的文件，单批最多 50 个文件",
    id: "Pilih beberapa file dengan jenis berbeda sekaligus, maksimal 50 file per batch",
  },
  /** 空态提示三行之二：格式与体积限制 */
  resUploadHintFormats: {
    en: "Supported formats: video MP4/MOV (5 sec–5 min, ≤500MB), image JPG, PNG, WEBP (≤30MB, max 6000px * 6000px), audio MP3/WAV (≤15MB)",
    zh: "支持格式：视频 MP4/MOV（5 秒~5 分钟，≤500MB）、图片 JPG、PNG、WEBP（≤30MB，最大宽高 6000px * 6000px）、音频 MP3/WAV（≤15MB）",
    id: "Format yang didukung: video MP4/MOV (5 dtk–5 mnt, ≤500MB), gambar JPG, PNG, WEBP (≤30MB, maks. 6000px * 6000px), audio MP3/WAV (≤15MB)",
  },
  /** 空态提示三行之三：音色说明 */
  resUploadHintVoice: {
    en: "MP3 files can be marked as voice in the list (2.0–5.0 sec, ≤10MB)",
    zh: "MP3 可在列表中标为音色（2.0～5.0 秒，≤10MB）",
    id: "MP3 dapat ditandai sebagai suara di daftar (2,0–5,0 dtk, ≤10MB)",
  },

  // ── 文件校验（materialFileRules / materialFileValidate）──
  /** 超出单批待提交上限的排除原因 */
  resUploadBatchLimit: {
    en: "Up to {n} files can be uploaded per batch",
    zh: "单批最多上传 {n} 个文件",
    id: "Maksimal {n} file per sekali unggah",
  },
  /** 空文件排除原因 */
  resUploadFileEmpty: { en: "File is empty", zh: "文件为空", id: "File kosong" },
  /** 视频扩展名不被支持 */
  resUploadVideoFormatUnsupported: {
    en: "Unsupported video format. Only MP4 and MOV are supported",
    zh: "不支持的视频格式，仅支持：MP4、MOV",
    id: "Format video tidak didukung, hanya mendukung: MP4, MOV",
  },
  /** 扩展名无法识别时的兜底原因 */
  resUploadFormatUnsupported: { en: "Unsupported format", zh: "格式不支持", id: "Format tidak didukung" },
  /** 音频扩展名与文件头不一致或不受支持 */
  resUploadAudioFormatMismatch: {
    en: "The actual audio format does not match the file name or is unsupported. Only {formats} are supported",
    zh: "音频实际格式与文件名不匹配或不受支持，仅支持：{formats}",
    id: "Format audio sebenarnya tidak cocok dengan nama file atau tidak didukung, hanya mendukung: {formats}",
  },
  /** 文件头内容不是允许的音频格式 */
  resUploadAudioContentInvalid: {
    en: "The file content is not a valid {formats} audio file. Please choose a valid audio file and try again.",
    zh: "文件内容不是有效的 {formats} 音频，请选择有效音频文件后重试。",
    id: "Isi file bukan audio {formats} yang valid. Silakan pilih file audio yang valid lalu coba lagi.",
  },
  /** 音频元数据读取失败 */
  resUploadAudioInfoUnreadable: { en: "Could not read the audio info", zh: "无法读取音频信息", id: "Tidak dapat membaca informasi audio" },
  /** 图片内容无法解码 */
  resUploadImageUnreadable: { en: "Could not read the image content", zh: "无法读取图片内容", id: "Tidak dapat membaca isi gambar" },
  /** 服务端视频上传会话创建失败 */
  resUploadVideoSessionFailed: {
    en: "Failed to create the video upload session. Please choose the video again",
    zh: "视频上传会话创建失败，请重新选择视频",
    id: "Gagal membuat sesi unggah video, silakan pilih video lagi",
  },
  /** 服务端视频预校验失败兜底 */
  resUploadVideoValidateFailed: { en: "Video validation failed", zh: "视频校验失败", id: "Validasi video gagal" },

  // ── 名称校验（materialUploadName）──
  /** 库内同名占用 */
  resUploadNameExist: {
    en: "This media name already exists. Please use another name",
    zh: "素材名称已存在，请使用其他名称",
    id: "Nama materi sudah ada, gunakan nama lain",
  },
  /** 本批待提交列表内部重名 */
  resUploadNameBatchDup: { en: "Duplicate names in this batch", zh: "本批文件中名称重复", id: "Ada nama yang duplikat di batch ini" },
  /** 名称留空 */
  resUploadNameEmpty: { en: "Please enter a media name", zh: "请填写素材名称", id: "Mohon isi nama materi" },
  /** 名称超过长度上限 */
  resUploadNameTooLong: {
    en: "The media name cannot exceed {n} characters",
    zh: "素材名称不能超过{n}个字符",
    id: "Nama materi tidak boleh lebih dari {n} karakter",
  },

  // ── 单文件上传弹窗 ──
  /** 文件类型选择器标签 */
  resUploadFileType: { en: "File type", zh: "文件类型", id: "Jenis file" },
  /** 类型选项：剪辑素材 */
  resUploadStickerType: { en: "Clip asset", zh: "剪辑素材", id: "Materi klip" },
  /** 视频表单：是否有口播/旁白标签 */
  resUploadHasNarrationLabel: { en: "Narration / voiceover?", zh: "是否有口播/旁白", id: "Ada sulih suara/narasi?" },
  /** 口播单选：否 */
  resUploadNo: { en: "No", zh: "否", id: "Tidak" },
  /** 未选择口播选项的提交拦截 */
  resUploadNarrationRequired: {
    en: "Please choose whether the video has narration / voiceover",
    zh: "请选择是否有口播/旁白",
    id: "Silakan pilih ada atau tidaknya sulih suara/narasi",
  },
  /** 商品六视图槽位标签：正面 */
  resUploadViewFront: { en: "Front", zh: "正面", id: "Depan" },
  /** 商品六视图槽位标签：背面 */
  resUploadViewBack: { en: "Back", zh: "背面", id: "Belakang" },
  /** 商品六视图槽位标签：左侧 */
  resUploadViewLeft: { en: "Left", zh: "左侧", id: "Kiri" },
  /** 商品六视图槽位标签：右侧 */
  resUploadViewRight: { en: "Right", zh: "右侧", id: "Kanan" },
  /** 商品六视图槽位标签：顶部 */
  resUploadViewTop: { en: "Top", zh: "顶部", id: "Atas" },
  /** 商品六视图槽位标签：细节 */
  resUploadViewCloseup: { en: "Detail", zh: "细节", id: "Detail" },
  /** 六视图区顶部说明 */
  resUploadProduct6Hint: {
    en: "Click each view slot to upload the matching image. At least 1 image is required",
    zh: "点击各视图格子上传对应图片，至少上传 1 张",
    id: "Klik setiap kotak tampilan untuk mengunggah gambar yang sesuai, minimal 1 gambar",
  },
  /** 六视图缺图的提交拦截 */
  resUploadProduct6Required: {
    en: "Upload the matching image in each view slot. At least 1 image is required",
    zh: "各视图格子上传对应图片，至少上传 1 张",
    id: "Unggah gambar yang sesuai di setiap kotak tampilan, minimal 1 gambar",
  },
  /** 六视图规格列表：格式 */
  resUploadSpecFormat: { en: "Format", zh: "格式", id: "Format" },
  /** 六视图规格列表：宽高比 */
  resUploadSpecRatio: { en: "Aspect ratio (width / height)", zh: "宽高比（宽度除以高度）", id: "Rasio aspek (lebar / tinggi)" },
  /** 六视图规格列表：宽高比取值 */
  resUploadSpecRatioValue: { en: "Greater than 0.4, less than 2.5", zh: "大于0.4，小于2.5", id: "Lebih dari 0,4, kurang dari 2,5" },
  /** 六视图规格列表：宽高长度 */
  resUploadSpecSize: { en: "Width / height (px)", zh: "宽高长度（px）", id: "Lebar / tinggi (px)" },
  /** 六视图规格列表：宽高取值 */
  resUploadSpecSizeValue: {
    en: "Greater than 300px, less than 6000px",
    zh: "大于300px, 小于6000px",
    id: "Lebih dari 300px, kurang dari 6000px",
  },
  /** 六视图规格列表：大小 */
  resUploadSpecFileSize: { en: "Size", zh: "大小", id: "Ukuran" },
  /** 六视图规格列表：单张体积 */
  resUploadSpecFileSizeValue: { en: "Each image smaller than 30 MB", zh: "单张图片小于 30 MB", id: "Setiap gambar kurang dari 30 MB" },
  /** 剪辑素材悬停替换提示 */
  resUploadStickerReplace: { en: "Click to replace the clip asset", zh: "点击替换剪辑素材", id: "Klik untuk mengganti materi klip" },
  /** 视频未选文件时提示前缀 */
  resUploadVideoHintHead: {
    en: "Supported formats: MP4, MOV | Max file size: ≤500MB | Video duration range: ",
    zh: "支持上传格式：MP4、MOV | 单文件大小上限：≤500MB | 视频时长范围：",
    id: "Format yang didukung: MP4, MOV | Batas ukuran file: ≤500MB | Rentang durasi video: ",
  },
  /** 视频未选文件时提示后缀 */
  resUploadVideoHintTail: { en: " | Frame rate: 24~60 fps", zh: " | 视频帧率：24~60 帧/秒", id: " | Frame rate: 24~60 fps" },
  /** 视频兼容性弹窗标题 */
  resUploadCompatTitle: { en: "Video compatibility notice", zh: "视频兼容性提示", id: "Pemberitahuan kompatibilitas video" },
  /** 视频兼容性弹窗正文 */
  resUploadCompatBody: {
    en: "This video has poor browser compatibility. If you choose “Continue upload”, it may show a black screen, fail to play, or display abnormally in some browsers. If you choose “Transcode & upload”, the system will convert it to a more compatible format while keeping the original resolution, aspect ratio, frame rate and audio as unchanged as possible. However, image quality, colors or file size may change slightly, and transcoding takes some time to finish.",
    zh: "该视频在浏览器中的兼容性较差，如果选择【继续上传】，可能会在部分浏览器中出现黑屏、无法播放或显示异常等问题。如果选择【转码上传】，系统会将该视频转换为兼容性更好的格式，并尽量保持原视频的分辨率、比例、帧率和音频不变，但画质、颜色或文件大小可能发生轻微变化，转码处理需要一定时间等待。",
    id: "Video ini kurang kompatibel dengan browser. Jika memilih “Lanjutkan unggahan”, dapat muncul layar hitam, video tidak dapat diputar, atau tampilan abnormal di sebagian browser. Jika memilih “Konversi & unggah”, sistem akan mengonversi video ke format yang lebih kompatibel dengan menjaga resolusi, rasio, frame rate, dan audio asli sedapat mungkin, namun kualitas gambar, warna, atau ukuran file dapat sedikit berubah, dan proses konversi memerlukan waktu.",
  },
  /** 兼容性弹窗：继续上传 */
  resUploadCompatContinue: { en: "Continue upload", zh: "继续上传", id: "Lanjutkan unggahan" },
  /** 兼容性弹窗：转码上传 */
  resUploadCompatTranscode: { en: "Transcode & upload", zh: "转码上传", id: "Konversi & unggah" },
  /** 系统预设标题：视频 */
  resUploadPresetVideo: { en: "Upload system preset video", zh: "上传系统预设视频", id: "Unggah video preset sistem" },
  /** 系统预设标题：图片 */
  resUploadPresetImage: { en: "Upload system preset image", zh: "上传系统预设图片", id: "Unggah gambar preset sistem" },
  /** 系统预设标题：剪辑素材 */
  resUploadPresetSticker: { en: "Upload system preset clip asset", zh: "上传系统预设剪辑素材", id: "Unggah materi klip preset sistem" },
  /** 系统预设标题：音频 */
  resUploadPresetAudio: { en: "Upload system preset audio", zh: "上传系统预设音频", id: "Unggah audio preset sistem" },
  /** 系统预设标题：音色 */
  resUploadPresetVoice: { en: "Upload system preset voice", zh: "上传系统预设音色", id: "Unggah suara preset sistem" },
  /** 系统预设标题兜底 */
  resUploadPresetDefault: { en: "Upload system preset", zh: "上传系统预设", id: "Unggah preset sistem" },
  /** 剪辑素材名称标签 */
  resUploadStickerName: { en: "Clip asset name", zh: "剪辑素材名称", id: "Nama materi klip" },
  /** 剪辑素材名称占位 */
  resUploadStickerNamePh: { en: "Enter the clip asset name", zh: "请输入剪辑素材名称", id: "Masukkan nama materi klip" },
  /** 剪辑素材描述标签 */
  resUploadStickerDesc: { en: "Clip asset description", zh: "剪辑素材描述", id: "Deskripsi materi klip" },
  /** 剪辑素材描述占位 */
  resUploadStickerDescPh: { en: "Enter the clip asset description", zh: "请输入剪辑素材描述", id: "Masukkan deskripsi materi klip" },
  /** 视频描述占位 */
  resUploadVideoDescPh: { en: "Enter the video description", zh: "请输入视频描述", id: "Masukkan deskripsi video" },
  /** 音频描述占位 */
  resUploadAudioDescPh: { en: "Enter the audio description", zh: "请输入音频描述", id: "Masukkan deskripsi audio" },
  /** 音色名称占位 */
  resUploadVoiceNamePh: { en: "Enter the voice name", zh: "请输入音色名称", id: "Masukkan nama suara" },
  /** 音色描述占位 */
  resUploadVoiceDescPh: { en: "Enter the voice description", zh: "请输入音色描述", id: "Masukkan deskripsi suara" },
  /** 剪辑素材上传提示 */
  resUploadStickerHint: {
    en: "Any format | Max file size: ≤10MB",
    zh: "不限格式 | 单文件大小上限：≤10MB",
    id: "Format bebas | Batas ukuran file: ≤10MB",
  },
  /** 音频上传提示 */
  resUploadAudioHint: {
    en: "Supported formats: MP3, WAV | Max file size: ≤15MB",
    zh: "支持上传格式：MP3、WAV | 单文件大小上限：≤15MB",
    id: "Format yang didukung: MP3, WAV | Batas ukuran file: ≤15MB",
  },
  /** 音色上传提示（{range} 为时长范围） */
  resUploadVoiceHint: {
    en: "Supported format: MP3 | Max file size: ≤10MB | Voice duration range: {range}",
    zh: "支持上传格式：MP3 | 单文件大小上限：≤10MB | 音色时长范围：{range}",
    id: "Format yang didukung: MP3 | Batas ukuran file: ≤10MB | Rentang durasi suara: {range}",
  },
  /** 视频时长范围：自定义上限 */
  resUploadVideoRangeSec: { en: "5 sec - {n} sec", zh: "5 秒 - {n} 秒", id: "5 dtk - {n} dtk" },
  /** 视频时长范围：默认 5 分钟 */
  resUploadVideoRangeDefault: { en: "5 sec - 5 min", zh: "5 秒 - 5 分钟", id: "5 dtk - 5 mnt" },
  /** 视频选中后校验兼容性阶段文案 */
  resUploadValidatingVideo: { en: "Checking video compatibility...", zh: "正在校验视频兼容性...", id: "Memeriksa kompatibilitas video..." },
  /** 视频超出调用方传入的时长上限 */
  resUploadVideoTooLong: {
    en: "The video duration cannot exceed {n} seconds",
    zh: "视频时长不能超过 {n} 秒",
    id: "Durasi video tidak boleh lebih dari {n} detik",
  },
  /** 兼容性差时等待用户选择处理方式 */
  resUploadAwaitingDecision: {
    en: "Waiting for how to handle the video...",
    zh: "等待选择视频处理方式...",
    id: "Menunggu pilihan cara menangani video...",
  },
  /** 用户选择转码后的阶段文案 */
  resUploadTranscodingUploading: {
    en: "Transcoding and uploading, please wait...",
    zh: "正在转码并上传，请耐心等待...",
    id: "Mengonversi dan mengunggah, mohon tunggu...",
  },
  /** 视频直接上传阶段文案 */
  resUploadUploadingVideo: { en: "Uploading video...", zh: "正在上传视频...", id: "Mengunggah video..." },
  /** 提交时视频会话尚未完成 */
  resUploadVideoNotReady: {
    en: "The video is not processed yet. Please choose the video again and retry",
    zh: "视频尚未处理完成，请重新选择视频后重试",
    id: "Video belum selesai diproses, silakan pilih video lagi lalu coba",
  },
} as const;
